import { isValidEmail, isValidPhone, getTerminalDateWithOffset } from "../../../utils/utils";
import {
  convertDateTimeToTimeString,
  getDateWithUtcHoursAndMinFromTimeString,
} from '@dataGrid/gridUtilities';
import {getDefaultPaymentInfo} from "./Payment/util"
import appConstants from "../../../../../../appConstants";
import { MIN_VALUE } from "../common/constants";

const getExceptionDataArray = (shipment) => {
  const { exceptionDetail, returnDetail } = shipment; 
  return exceptionDetail || (returnDetail ? [returnDetail] : []);
};

const getDefaultDeliveryStopInfo = ({ stopData, getDefaultExceptionDetail, getAccessorialsArray }) => {
  return stopData.shipments.map((shipment) => {
    const isExcessiveLength =
      !shipment.excessiveLength ||
      shipment.excessiveLength === appConstants.EXCESSIVE_SHIPMENT.CAN_NOT_SEE
        ? appConstants.EXCESSIVE_SHIPMENT.NO
        : shipment.excessiveLength;
    
    const exceptionData = getExceptionDataArray(shipment);
    return {
      proId: shipment.proId,
      editInfo: {
        deliveryStatus: shipment.deliveryCode || '',
        exceptionType: shipment.exceptionCode || '',
        accessorials: getAccessorialsArray(shipment.accessorials) || [],
        sortAndSegregatePieces: shipment.sortAndSegregatePieces || null,
        limitedAccessCode: shipment.limitedAccessCode || '',
        liftgateReasonCode: shipment.liftgateReasonCode || '',
        returnCode: shipment.returnCode || '',
        customerName: shipment.returnDetail?.displayCustomerName || null,
        isShrinkWrap: shipment.isShrinkWrap || false,
        isPhysicalPiecesChanged: shipment.isPhysicalPiecesChanged || false,
        isExcessiveLength,
        excessiveLength: isExcessiveLength ? shipment.excessiveLengthValue: null,
        excessiveLengthInches: isExcessiveLength ? shipment.excessiveLengthInches: null,
        palletCount: shipment.palletCount || '',
        physicalPiecesHandled: shipment.physicalPiecesHandled ?? null,
        exception: getDefaultExceptionDetail(exceptionData),
        ...getDefaultPaymentInfo(shipment.paymentDetails || {}),
      },
      errorInfo: {}
    };
  });
};

const getDefaultStopTimeInfo = (timeObj = {}) => {
  const { isDriverArrived, actualArrivalTime, departureTime, isStopCompleted } = timeObj;
  return {
    stopTime: {
      arrivalTime:
        (isDriverArrived || isStopCompleted) && actualArrivalTime
          ? getDateWithUtcHoursAndMinFromTimeString(actualArrivalTime)
          : null,
      departureTime: departureTime ? getDateWithUtcHoursAndMinFromTimeString(departureTime) : null
    },
    stopTimeError: {
      arrivalTime: null,
      departureTime: null,
    },
  };
};

const getDepartureTimeError = (value, { arrivalTime }, isStopCompleted) => {
  if (!value) {
    return "* Required";
  }
  const parseDate = (dateString) => {
    const date = new Date(dateString);
    date.setSeconds(0, 0); // Set seconds and milliseconds to zero
    return date;
  };
  const dateValue = parseDate(value);
  const dateArrivalTime = parseDate(arrivalTime);
  if (isStopCompleted && dateValue < dateArrivalTime) {
    return `Departure Time should be greater than and equal to ${convertDateTimeToTimeString(
      arrivalTime
    )}`;
  }
  if (!isStopCompleted && dateValue <= dateArrivalTime) { 
    return `Departure Time should be greater than ${convertDateTimeToTimeString(
      arrivalTime
    )}`;
  }
  return null;
};

const getStopTimeFieldError = ({field, value, stopTimeInfo, isStopCompleted = false}) => {
  switch (field) {
    case "arrivalTime":
      return value ? null : "* Required";
    case "departureTime":
      return getDepartureTimeError(value, stopTimeInfo, isStopCompleted);
    default:
      return null;
  }
};
const getStopTimeErrorInfo = (stopTime) => {
  let errorInfo = {};
  Object.keys(stopTime).forEach((fieldName) => {
    errorInfo[fieldName] = getStopTimeFieldError({
      field: fieldName,
      value: stopTime[fieldName],
      stopTimeInfo: stopTime
    });
  });
  return { stopTime, stopTimeError: errorInfo };
};

const isStopTimeDetailsInValid = (stopTimeDetails) => {
  const error = Object.values(stopTimeDetails.stopTimeError).filter(
    (value) => value
  ).length;
  return Boolean(error);
};

const getDefaultReceiverDetailsInfo = (defaultSCACCode = '', receiverDetails = {}) => {
  return {
    receiver: {
      receiverId: receiverDetails.receiverId ?? null,
      receiverName: receiverDetails.receiverName ?? null,
      receiverEmail: receiverDetails.receiverEmail ?? null,
      receiverCellPhone: receiverDetails.receiverCellPhone ?? null,
      scacCode: defaultSCACCode ?? ''
    },
    receiverError: {
      receiverName: null,
      receiverEmail: null,
      receiverCellPhone: null,
      scacCode: null,
    },
  };
};

const getReceiverEmailError = (value) => {
  if (value && !isValidEmail(value)) {
    return "Invalid email";
  }
  return null;
};


const getReceiverNumberError = (value) => {
  if (value && !isValidPhone(value)) {
    return "Invalid phone number";
  }
  return null;
};

const getReceiverDetailsFieldError = (field, value, validateScacCode, showTrailerScac) => {
  switch (field) {
    case "receiverName":
      return value ? null : "* Required";
    case "receiverEmail":
      return getReceiverEmailError(value);
    case "receiverCellPhone":
      return getReceiverNumberError(value);
    case "scacCode":
      return showTrailerScac && validateScacCode && !value ? "* Required" : null;
    default:
      return null;
  }
};

const getReceiverDetailsErrorInfo = (receiver, validateScacCode, showTrailerScac) => {
  let errorInfo = {};
  Object.keys(receiver).forEach((fieldName) => {
    errorInfo[fieldName] = getReceiverDetailsFieldError(
      fieldName,
      receiver[fieldName],
      validateScacCode,
      showTrailerScac
    );
  });
  return { receiver, receiverError: errorInfo };
};

const isReceiverDetailsInValid = (receiverDetails) => {
  const error = Object.values(receiverDetails.receiverError).filter(
    (value) => value
  ).length;
  return Boolean(error);
};

const getProLabelAndValue = (shipments) => {
  return {
    proDetailsLabel: "Pro Number(s)",
    proDetailsValue: shipments.map((pro) => pro.proId)?.join("; ") || "",
  };
};

const getShipmentFreightBill = (freightBillData, proId) => {
  return freightBillData.find(
    (proFreightBill) => proFreightBill.proId === proId
  )?.freightBillDetails;
};

const getExcessiveLengthError = (value, maxLength) => {
  if (!value || value < MIN_VALUE.EXCESSIVE_LENGTH.FEET) {
    return "Enter a value more than or equal to 8 feet";
  } else if (value > maxLength) {
    return `Enter a value less than or equal to ${maxLength} feet`;
  }
  return null;
};

const getStopTimeWithOffset = ({ arrivalTime, departureTime }, offset) => {
  return {
    arrivalTime: arrivalTime
      ? getTerminalDateWithOffset(arrivalTime, offset)
      : arrivalTime,
    departureTime: departureTime
      ? getTerminalDateWithOffset(departureTime, offset)
      : departureTime
  };
};

const getStopHeaderText=(isCompletedStop)=>{
  return isCompletedStop? "Reprocess a delivery" : "Process a delivery"
}


export {
  getDefaultDeliveryStopInfo,
  getDefaultStopTimeInfo,
  getDefaultReceiverDetailsInfo,
  getReceiverDetailsFieldError,
  getReceiverDetailsErrorInfo,
  isReceiverDetailsInValid,
  getStopTimeErrorInfo,
  isStopTimeDetailsInValid,
  getStopTimeFieldError,
  getProLabelAndValue,
  getDepartureTimeError,
  getShipmentFreightBill,
  getExcessiveLengthError,
  getStopTimeWithOffset,
  getStopHeaderText,
};
